// P:\0PJ\synforge\frontend\app\settings\stats\page.tsx v1 2026.07.27 06:16 - moved from dashboard/page.tsx, renamed to StatsPage
//   대시보드 상단 통계 + 최근 회의/결과/일정을 설정 안의 별도 통계 섹션으로 이전.
//   usage(사용량/과금)와는 성격이 다르다 - 이건 "회의 활동 현황", usage 는 "토큰/과금".
//   로직·API 호출은 dashboard/page.tsx 원본과 동일, 컴포넌트명만 변경.
"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import {
  FolderOpen, Cpu, MessageSquare, TrendingUp,
  ArrowRight, FileText, Loader2, Plus
} from "lucide-react"
import { DashboardData } from "@/types"
import { useRouter } from "next/navigation"
import { authAPI, dashboardAPI } from "@/lib/api"
import { useLocale } from '@/contexts/LocaleContext'

const statusColor = (status: string) => {
  if (status === "done")    return "text-green-400"
  if (status === "running") return "text-amber-400"
  if (status === "failed")  return "text-red-400"
  return "text-white/40"
}

export default function StatsPage() {
  const router = useRouter()
  const { t } = useLocale()

  const [data,     setData]     = useState<DashboardData | null>(null)
  const [loading,  setLoading]  = useState(true)
  const [userName, setUserName] = useState("")

  useEffect(() => {
    const load = async () => {
      try {
        const meRes = await authAPI.me()
        setUserName(meRes.data.name)
        const res = await dashboardAPI.get()
        setData(res.data)
      } catch (e: any) {
        if (e.response?.status === 403 || e.response?.status === 401) {
          router.push("/login")
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // status 라벨 번역
  const statusLabel = (status: string) => {
    if (status === "done")    return t("dashboard.completed")
    if (status === "running") return t("common.loading")
    if (status === "failed")  return "Failed"
    return "Pending"
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-purple-400" size={32} />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[1200px]">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white mb-1">
          {t("dashboard.welcomeBack")} <span className="text-purple-400">{userName}</span>
        </h1>
        <p className="text-white/40 text-sm">
          {t("dashboard.subtitle")}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          {
            icon: FolderOpen,
            label: t("dashboard.activeProjects"),
            value: data?.active_projects ?? 0,
            color: "text-purple-400",
            bg: "bg-purple-500/10",
          },
          {
            icon: Cpu,
            label: t("dashboard.aiTeams"),
            value: data?.active_ai_count ?? 0,
            color: "text-blue-400",
            bg: "bg-blue-500/10",
          },
          {
            icon: MessageSquare,
            label: t("dashboard.todaysMeetings"),
            value: data?.today_sessions ?? 0,
            color: "text-green-400",
            bg: "bg-green-500/10",
          },
          {
            icon: TrendingUp,
            label: t("dashboard.avgAgreement"),
            value: data?.avg_agreement ? `${Math.round(data.avg_agreement)}%` : "--",
            color: "text-amber-400",
            bg: "bg-amber-500/10",
          },
        ].map((card, i) => (
          <div key={i} className="bg-[#0f0f18] border border-white/[0.07] rounded-xl p-5">
            <div className={`w-9 h-9 ${card.bg} rounded-lg flex items-center justify-center mb-3`}>
              <card.icon size={18} className={card.color} />
            </div>
            <div className="text-2xl font-bold text-white mb-1">{card.value}</div>
            <div className="text-xs text-white/40">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Recent Meetings */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{t("dashboard.recentMeetings")}</h2>
          <Link href="/history" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
            {t("common.viewAll")} <ArrowRight size={12} />
          </Link>
        </div>

        <div className="grid grid-cols-4 gap-4">
          {data?.recent_sessions?.slice(0, 3).map((session) => (
            <Link
              key={session.id}
              href={`/meeting/${session.id}`}
              className="bg-[#0f0f18] border border-white/[0.07] rounded-xl p-4 hover:border-purple-500/30 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-500/15 rounded-lg flex items-center justify-center mb-3">
                <MessageSquare size={18} className="text-purple-400" />
              </div>
              <div className="font-semibold text-white text-sm mb-1 line-clamp-2">
                {session.agenda}
              </div>
              <div className={`text-xs ${statusColor(session.status)} mb-3`}>
                {statusLabel(session.status)}
              </div>
              {session.agreement_pct !== null && session.agreement_pct !== undefined && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/40">{t("dashboard.agreement")}</span>
                    <span className="text-white/70">{Math.round(session.agreement_pct)}%</span>
                  </div>
                  <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${session.agreement_pct}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="w-full text-xs text-white/50 border border-white/[0.07] rounded-lg py-1.5 text-center">
                {session.status === "done" ? t("dashboard.viewResult") : t("common.next")}
              </div>
            </Link>
          ))}

          {/* New Meeting Card */}
          <Link
            href="/meeting/snew"
            className="bg-transparent border border-dashed border-white/[0.12] rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:border-purple-500/40 hover:bg-purple-500/5 transition-colors min-h-[180px]"
          >
            <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center">
              <Plus size={18} className="text-purple-400" />
            </div>
            <span className="text-xs text-white/40">{t("dashboard.newMeeting")}</span>
          </Link>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-2 gap-6">

        {/* Recent Results */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white">{t("dashboard.recentResults")}</h2>
            <Link href="/results" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
              {t("common.viewAll")} <ArrowRight size={12} />
            </Link>
          </div>
          <div className="bg-[#0f0f18] border border-white/[0.07] rounded-xl overflow-hidden">
            {!data?.recent_results?.length ? (
              <div className="p-8 text-center text-white/30 text-sm">
                {t("dashboard.noResults")}
              </div>
            ) : (
              data.recent_results.map((result: any, i: number) => (
                <div key={i} className="flex items-center gap-3 p-3 border-b border-white/[0.05] last:border-0 hover:bg-white/[0.02]">
                  <div className="w-8 h-8 bg-white/[0.04] rounded-lg flex items-center justify-center">
                    <FileText size={14} className="text-white/40" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{result.name}</div>
                    <div className="text-xs text-white/30">{result.file_type?.toUpperCase()}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Today's Schedule */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white">{t("dashboard.todaysSchedule")}</h2>
            <Link href="/history" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1">
              {t("common.viewAll")} <ArrowRight size={12} />
            </Link>
          </div>
          <div className="bg-[#0f0f18] border border-white/[0.07] rounded-xl overflow-hidden">
            {!data?.recent_sessions?.length ? (
              <div className="p-8 text-center text-white/30 text-sm">
                {t("dashboard.noResults")}
              </div>
            ) : (
              data.recent_sessions.slice(0, 4).map((session, i) => (
                <div key={i} className="flex items-center gap-3 p-3 border-b border-white/[0.05] last:border-0">
                  <div className="text-xs text-white/30 w-12 shrink-0">
                    {new Date(session.created_at).toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{session.agenda}</div>
                    <div className={`text-xs ${statusColor(session.status)}`}>{statusLabel(session.status)}</div>
                  </div>
                  <Link href={`/meeting/${session.id}`} className="text-xs text-purple-400 hover:text-purple-300 shrink-0">
                    {session.status === "done" ? t("dashboard.viewResult") : "Go"} →
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}